import PropTypes from 'prop-types';
import {
  Button,
  ICON_NAMES,
  INLINE_ALERT_VARIANTS,
  InlineAlert,
} from '@reverse-hr/design-system';
import { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export const DownloadFileButton = ({
  className,
  fileName = '',
  iconPosition = 'left',
  label,
  onClick,
  variant = 'ghost',
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchFailed, setIsFetchFailed] = useState(null);

  const wrapperClassNames = classNames('c-download-file-button', className, {
    'c-download-file-button--loading': isLoading,
  });

  const icon = isLoading ? ICON_NAMES.REFRESH : ICON_NAMES.DOWNLOAD;
  const isIconOnRight = iconPosition === 'right';
  const leftIcon = isIconOnRight ? undefined : icon;
  const rightIcon = isIconOnRight ? icon : undefined;

  const onDownloadClick = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setIsFetchFailed(false);

    try {
      const { filename: fetchedFileName, url } = await onClick();
      const virtualLink = document.createElement('a');

      virtualLink.href = url;
      virtualLink.download = fileName || fetchedFileName;
      virtualLink.click();
    } catch (error) {
      setIsFetchFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={wrapperClassNames}>
      <Button
        leftIcon={leftIcon}
        onClick={onDownloadClick}
        rightIcon={rightIcon}
        variant={variant}
      >
        {label}
      </Button>

      {isFetchFailed && (
        <InlineAlert
          className="c-download-file-button__alert"
          icon={ICON_NAMES.WARNING_OUTLINE}
          isClosable
          isFloating
          title={t('general.error.title')}
          variant={INLINE_ALERT_VARIANTS.ERROR}
        />
      )}
    </div>
  );
};

DownloadFileButton.propTypes = {
  className: PropTypes.string,
  fileName: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};
